import { useParams } from 'react-router-dom';
import { APIErrorType } from '../../api';
import { StudentQuestionType } from '../../api/student-question';
import { useLessons } from '../../hooks/react-query/use-lesson';
import { useStudentExam } from '../../hooks/react-query/use-student-exam';
import { useStudentQuestionsInfiniteQuery } from '../../hooks/react-query/use-student-question';
import { useStudentQuestionFilters } from '../../hooks/index/use-student-question-filters';
import { useVoices } from '../../hooks/react-query/use-voices';
import Error from '../shared/error';
import Loader from '../shared/loader';
import Segment from '../shared/segment';
import StudentQuestionsFilter from '../shared/student-exam-filter/filter';
import Actions from './actions';
import ExamHeader from './exam-header';
import LessonTitle from './lesson-title';
import StudentQuestion from './student-question';
import Voices from './voices';

const QUESTIONS_PER_PAGE = 10;
const REVIEWABLE_EXAM_STATUSES = [2, 3];

function ExamReviewContainer() {
  const { examId } = useParams();
  const parsedExamId = examId ? parseInt(examId) : undefined;

  const {
    isError: studentExamIsError,
    error: studentExamError,
    isLoading: studentExamIsLoading,
    data: studentExam
  } = useStudentExam(parsedExamId);

  const { data: voices } = useVoices(parsedExamId);
  const { data: lessonData } = useLessons();

  const { queryParams, panelProps, showAllAnswers } = useStudentQuestionFilters();

  const {
    isError: studentQuestionsIsError,
    error: studentQuestionsError,
    isLoading: studentQuestionsIsLoading,
    data: studentQuestionsData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useStudentQuestionsInfiniteQuery<StudentQuestionType>({
    ...(parsedExamId !== undefined && { examId: parsedExamId }),
    ...queryParams
  });

  if (!examId) return <Error />;
  if (!lessonData) return null;

  if (studentExamIsError) {
    const error = studentExamError as APIErrorType;
    return error.status === 403 ? (
      <Error errorMessage='شما دسترسی به این آزمون ندارید.' />
    ) : (
      <Error error={error} />
    );
  }
  if (studentQuestionsIsError) return <Error error={studentQuestionsError as APIErrorType} />;
  if (studentExamIsLoading || !studentExam) return <Loader />;

  if (!REVIEWABLE_EXAM_STATUSES.includes(studentExam.status))
    return <Error errorMessage='شما دسترسی به سوالات این آزمون ندارید.' />;

  const examLessonIds = new Set(studentExam.exam.lessons);
  const examLessons = lessonData.filter(lesson => examLessonIds.has(lesson.id));
  const hasNoResults = studentQuestionsData?.pages[0].count === 0;

  let lastLessonId: number | null = null;

  return (
    <div className='mx-2 flex flex-col gap-1'>
      <ExamHeader exam={studentExam.exam} />
      <StudentQuestionsFilter
        {...panelProps}
        initialIsOpen={false}
        lessonData={examLessons}
      />
      {hasNoResults && (
        <Segment textAlign='center' noMargin>
          <p>بر اساس فیلترهای شما هیچ سوالی یافت نشد</p>
          <p>لطفا فیلترها را تغییر دهید</p>
        </Segment>
      )}
      {studentQuestionsData?.pages.map((page, pageIndex) => (
        <div key={pageIndex}>
          {page.data.map((studentQuestion, questionIndex) => {
            const hasNewLesson = studentQuestion.question.lesson.id !== lastLessonId;
            lastLessonId = studentQuestion.question.lesson.id;

            return (
              <div key={studentQuestion.id}>
                {studentExam.exam.lessonCount > 1 && hasNewLesson && (
                  <LessonTitle lesson={studentQuestion.question.lesson} />
                )}
                <StudentQuestion
                  studentQuestion={studentQuestion}
                  questionNumber={QUESTIONS_PER_PAGE * pageIndex + questionIndex + 1}
                  questionCount={page.count}
                  showAllAnswers={showAllAnswers}
                  showLessonName={false}
                />
              </div>
            );
          })}
        </div>
      ))}
      {(isFetchingNextPage || studentQuestionsIsLoading || !studentQuestionsData) && (
        <Loader full={false} />
      )}
      <Segment bg noMargin className='mb-36'>
        <Actions hasNextPage={hasNextPage} onFetchNext={fetchNextPage} />
      </Segment>
      {voices && <Voices voices={voices} />}
    </div>
  );
}

export default ExamReviewContainer;
